<!--
  Check-report panel of the provision configuration (04-frontend.md §6, navItem `configCheck` of
  the ProvisionType object page — not a top-level nav item of the config page).
  Built after the ns precedent ns-poc-config-check.component.html (PAT §I): counter header row,
  filter bar, entry list with a colored severity border.

  Severity and check code are <p-tag> — the same build the bd reference uses for status chips
  (bd-run-history-tab.component.html). TagModule IS reachable here: CpModule imports CoreModule,
  CoreModule exports SharedModule, SharedModule exports NgPrimeUltimaModule and that module
  exports TagModule.
-->
<div class="cp-config-check-panel flex flex-column gap-3 h-full">

    @if (!provisionTypeId) {

        <!-- Nothing selected: the panel needs exactly one provision type (endpoint is per-type). -->
        <div class="surface-card border-round-lg p-5 flex flex-column align-items-center gap-3 text-center">
            <i class="fa-solid fa-clipboard-check text-4xl text-color-secondary"></i>
            <span class="text-lg font-semibold">Konfigurationsprüfung</span>
            <span class="text-color-secondary">
                Bitte einen Provisionstyp wählen — der Prüfbericht wird je Provisionstyp erstellt.
            </span>
        </div>

    } @else if (busy) {

        <div class="flex flex-column gap-3">
            <p-skeleton height="4rem"></p-skeleton>
            <p-skeleton height="3rem"></p-skeleton>
            <p-skeleton height="3rem"></p-skeleton>
        </div>

    } @else if (loadError) {

        <div class="surface-card border-round-lg p-5 flex flex-column align-items-center gap-3 text-center">
            <i class="fa-solid fa-plug-circle-exclamation text-4xl text-red-500"></i>
            <span class="text-lg font-semibold">{{ loadError }}</span>
            <span class="text-color-secondary text-sm">
                Der Prüfbericht wird über <span class="font-medium">GET /mvsa/cp/configCheck/provisionType/{{ provisionTypeId }}</span> geladen.
            </span>
            <p-button label="Erneut prüfen"
                      icon="fa-solid fa-rotate"
                      severity="primary"
                      (onClick)="refresh()">
            </p-button>
        </div>

    } @else if (result) {

        <!-- Summary: errors / warnings / hints / overall verdict -->
        <div class="cp-config-check-summary flex align-items-center gap-4 py-3 px-3 surface-50 border-round-sm border-bottom-1 border-200">

            <div class="flex align-items-center gap-2"
                 [class.text-red-500]="result.totalErrors > 0"
                 [class.text-color-secondary]="result.totalErrors === 0">
                <i class="fa-solid fa-circle-xmark"></i>
                <span class="font-bold">{{ result.totalErrors }}</span>
                <span class="text-sm">Fehler</span>
            </div>

            <div class="border-left-1 border-300 h-1rem"></div>

            <div class="flex align-items-center gap-2"
                 [class.text-yellow-600]="result.totalWarnings > 0"
                 [class.text-color-secondary]="result.totalWarnings === 0">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <span class="font-bold">{{ result.totalWarnings }}</span>
                <span class="text-sm">Warnungen</span>
            </div>

            <div class="border-left-1 border-300 h-1rem"></div>

            <div class="flex align-items-center gap-2 text-color-secondary">
                <i class="fa-solid fa-circle-info"></i>
                <span class="font-bold">{{ getInfoCount() }}</span>
                <span class="text-sm">Hinweise</span>
            </div>

            <div class="border-left-1 border-300 h-1rem"></div>

            <div class="flex align-items-center gap-2">
                @if (result.totalErrors === 0 && result.totalWarnings === 0) {
                    <i class="fa-solid fa-circle-check text-green-500"></i>
                    <span class="font-bold text-green-500">Einsatzbereit</span>
                } @else {
                    <i class="fa-solid fa-clock text-color-secondary"></i>
                    <span class="text-color-secondary text-sm">Unvollständig</span>
                }
            </div>

            <div class="flex-1"></div>

            <span class="text-sm text-color-secondary">
                {{ result.checksRun }} Prüfungen · {{ result.provisionTypeName }}
            </span>

        </div>

        <!-- Filter bar + refresh -->
        <div class="flex align-items-center justify-content-between">

            <!--
              Severity segment filter as a PrimeNG control (UX1): keyboard operable and exposed to
              screen readers, which the previous clickable <div> row was not. `ngModel` is allowed
              here because this is a filter control, never an entity form.
            -->
            <p-selectButton [options]="filterOptions"
                            [(ngModel)]="activeFilter"
                            optionLabel="label"
                            optionValue="value"
                            [allowEmpty]="false"
                            size="small"
                            aria-label="Befunde nach Schweregrad filtern"
                            (onChange)="setFilter(activeFilter)">
            </p-selectButton>

            <p-button icon="fa-solid fa-rotate"
                      severity="secondary"
                      [text]="true"
                      [rounded]="true"
                      size="small"
                      pTooltip="Prüfung aktualisieren"
                      ariaLabel="Prüfung aktualisieren"
                      (onClick)="refresh()">
            </p-button>

        </div>

        <!-- Entry list CR-001…CR-032 -->
        @if (!filteredEntries.length) {
            <div class="surface-card border-round-lg p-4 flex flex-column align-items-center gap-2 text-center">
                @if (activeFilter === 'all') {
                    <i class="fa-solid fa-circle-check text-3xl text-green-500"></i>
                    <span class="text-lg font-semibold text-green-600">Alle Prüfungen bestanden</span>
                    <span class="text-color-secondary text-sm">
                        Die Konfiguration dieses Provisionstyps ist vollständig.
                    </span>
                } @else {
                    <i class="fa-solid fa-filter text-3xl text-color-secondary"></i>
                    <span class="text-color-secondary">Keine Einträge in dieser Auswahl.</span>
                }
            </div>
        } @else {
            <div class="cp-config-check-entries flex flex-column gap-2">
                @for (entry of filteredEntries; track entry.checkCode + '|' + entry.message + '|' + (entry.referenceEntityId || 0)) {
                    <div class="surface-card border-round-lg p-3 border-left-3"
                         [class.border-red-500]="getSeverity(entry) === cSeverityError"
                         [class.border-yellow-500]="getSeverity(entry) === cSeverityWarning"
                         [class.border-blue-500]="getSeverity(entry) === cSeverityInfo">

                        <div class="flex align-items-start gap-3">

                            <i [class]="getSeverityIcon(entry)" class="text-lg mt-1"></i>

                            <div class="flex flex-column gap-1 flex-1">

                                <div class="flex align-items-center gap-2 flex-wrap">
                                    <span class="font-medium">{{ entry.message }}</span>
                                    @if (entry.checkCode) {
                                        <p-tag [value]="entry.checkCode" severity="secondary"></p-tag>
                                    }
                                    <p-tag [value]="getSeverityLabel(entry)"
                                           [severity]="getSeverityTagSeverity(entry)">
                                    </p-tag>
                                </div>

                                @if (entry.referenceEntityType && entry.referenceEntityId) {
                                    <!-- Jump to the affected object (standard object navigation, ns precedent). -->
                                    <div class="flex align-items-center gap-2 text-sm flex-wrap">
                                        <a class="cp-config-check-jump inline-flex align-items-center gap-1 text-primary cursor-pointer"
                                           pTooltip="Betroffenes Objekt öffnen"
                                           tooltipPosition="top"
                                           (click)="openReference(entry)">
                                            <i class="fa-solid fa-arrow-up-right-from-square"></i>
                                            <span>{{ entry.referenceEntityName || entry.referenceEntityType }}</span>
                                        </a>

                                        <!--
                                          CR-033 is the ONE actionable rule of the catalog: its findings
                                          are the worklist an operator works off, one guided reset per
                                          event row (FD-7). Every other finding stops at the jump above.
                                          The button opens the dialog; it never writes here.
                                          It stays visible after a 403 and is disabled with the German
                                          reason, per the module's optimistic rights pattern.
                                        -->
                                        @if (isResettableEntry(entry)) {
                                            <p-button label="Berechnete Vermittler neu ermitteln"
                                                      icon="fa-regular fa-arrows-rotate"
                                                      severity="secondary"
                                                      [outlined]="true"
                                                      size="small"
                                                      [disabled]="!canAgentChange"
                                                      [pTooltip]="canAgentChange
                                                          ? 'Vorschau öffnen und diese Ereigniszeile bei Bedarf zur Neuermittlung freigeben — die Freigabe ergänzt nur, bestehende Sätze bleiben unverändert.'
                                                          : resetDeniedMessage"
                                                      tooltipPosition="top"
                                                      (onClick)="openResetDialog(entry)">
                                            </p-button>
                                        }
                                    </div>
                                }

                            </div>

                        </div>

                    </div>
                }
            </div>
        }

    }

    <!--
      Guided reset opened from a CR-033 finding (FD-7). ONE instance for the whole list: the dialog
      is bound to the id of the row that opened it and rebuilds preview and form on every open, so a
      single instance can serve every finding without carrying state between them.
    -->
    <cp-provision-calculated-agent-reset-dialog
            [(visible)]="resetDialogVisible"
            [provisionContractId]="resetProvisionContractId"
            [provisionContractLabel]="resetProvisionContractLabel"
            [canAgentChange]="canAgentChange"
            (onReset)="handleResetDone()"
            (onForbidden)="handleResetForbidden()">
    </cp-provision-calculated-agent-reset-dialog>

</div>
